import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { endpointsCf } from "../config/componentsConfig";
import { Task, TaskStatus, TaskType } from "../types/taskTypes";
import {
  LEGAL_JOBS_SYNC_NOW_EVENT,
  clearLegalActiveSseTaskId,
  clearPendingLegalTaskSession,
} from "./legalSearchTaskFlow";

/** Même signature que `useFetchWithMsal().execute`. */
export type ExhaustiveCancelExecute = (
  method: string,
  endpoint: string,
  requestData?: object
) => Promise<unknown>;

/**
 * Résout l’id de job comme sur la carte tâche : d’abord la tâche LEGAL_SEARCH
 * en cours pour ce fil (la plus récente), puis refs / session.
 */
export function resolveExhaustiveTaskIdForThread(
  tasks: Task[],
  threadId: string,
  pendingRef: string | null,
  sessionTaskId: string | null
): string | undefined {
  const candidates = tasks.filter(
    (t) =>
      t.type === TaskType.LEGAL_SEARCH &&
      t.threadId === threadId &&
      (t.status === TaskStatus.PROCESSING ||
        t.status === TaskStatus.SUMMARIZING)
  );
  candidates.sort(
    (a, b) =>
      (b.updatedAt ?? b.createdAt) - (a.updatedAt ?? a.createdAt)
  );
  if (candidates[0]?.id) return String(candidates[0].id);
  if (pendingRef) return pendingRef;
  if (sessionTaskId) return sessionTaskId;
  return undefined;
}

/**
 * Même flux que le bouton « Cancel » sur la carte tâche :
 * POST `/chat/exhaustive-search/cancel` (avec `userId` injecté par execute),
 * puis état local + session + sync ES.
 *
 * À appeler **avant** `interruptStreamForThread` pour que le serveur marque
 * `cancel_requested` / arrête le runner avant de couper le SSE côté client.
 */
export async function cancelExhaustiveSearchJob(
  execute: ExhaustiveCancelExecute,
  options: {
    threadId: string;
    taskId: string | undefined;
    setTasks: Dispatch<SetStateAction<Task[]>>;
    pendingLegalTaskIdRef?: MutableRefObject<string | null>;
    pendingLegalTaskIdsRef?: MutableRefObject<Set<string>>;
  }
): Promise<void> {
  const {
    threadId,
    taskId,
    setTasks,
    pendingLegalTaskIdRef,
    pendingLegalTaskIdsRef,
  } = options;

  try {
    await execute("POST", endpointsCf.exhaustiveSearchCancel(), {
      threadId,
      taskId,
    });
  } catch (e) {
    console.warn("[exhaustive] cancel POST failed", e);
  } finally {
    const now = Date.now();
    setTasks((prev) =>
      prev.map((t) => {
        if (t.type !== TaskType.LEGAL_SEARCH || t.threadId !== threadId) {
          return t;
        }
        const running =
          t.status === TaskStatus.PROCESSING ||
          t.status === TaskStatus.SUMMARIZING;
        const byKnownId =
          taskId &&
          t.id === taskId &&
          t.status !== TaskStatus.COMPLETED &&
          t.status !== TaskStatus.CANCELLED;
        if (!running && !byKnownId) return t;
        return {
          ...t,
          updatedAt: now,
          status: TaskStatus.CANCELLED,
          file: {
            ...t.file,
            status: "cancelled",
          },
        };
      })
    );
    clearPendingLegalTaskSession();
    clearLegalActiveSseTaskId();
    if (pendingLegalTaskIdRef) pendingLegalTaskIdRef.current = null;
    if (taskId && pendingLegalTaskIdsRef) {
      pendingLegalTaskIdsRef.current.delete(taskId);
    }
    try {
      window.dispatchEvent(new CustomEvent(LEGAL_JOBS_SYNC_NOW_EVENT));
    } catch {
      /* ignore */
    }
  }
}
